import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { AuthContext, API } from "../App";
import { Button } from "../components/ui/button";
import {
  ArrowLeft, Activity, Trophy, Flame, Gamepad2, UserPlus, Star, Clock
} from "lucide-react";
import { toast } from "sonner";

const FEED_ICONS = {
  achievement: Trophy,
  streak_milestone: Flame,
  game_score: Gamepad2,
  new_member: UserPlus,
};

const FEED_COLORS = {
  achievement: "text-amber-500 bg-amber-100 dark:bg-amber-900/30",
  streak_milestone: "text-orange-500 bg-orange-100 dark:bg-orange-900/30",
  game_score: "text-indigo-500 bg-indigo-100 dark:bg-indigo-900/30",
  new_member: "text-sky-500 bg-sky-100 dark:bg-sky-900/30",
};

const timeAgo = (ts) => {
  if (!ts) return "";
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString();
};

const SocialFeed = () => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeed = async () => {
      try {
        const res = await axios.get(`${API}/feed?limit=30`);
        setFeed(res.data.feed || []);
      } catch (err) {
        if (err.response?.status !== 401) toast.error("Failed to load feed");
      } finally {
        setLoading(false);
      }
    };
    fetchFeed();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-sky-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-sky-400/30 glow-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sky-50 dark:bg-slate-900" data-testid="social-feed-page">
      <div className="fixed top-0 right-0 w-96 h-96 bg-sky-300/15 rounded-full blur-3xl pointer-events-none" />

      <header className="fixed top-0 w-full z-50 glass-header">
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-sky-600 hover:text-sky-800 dark:text-sky-400" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
            {t("common.dashboard")}
          </Button>
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-sky-400" strokeWidth={1.5} />
            <span className="text-sm text-sky-700 dark:text-sky-300">Activity Feed</span>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 md:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="mb-6">
            <h1 className="text-2xl font-light text-sky-900 dark:text-sky-100" style={{ fontFamily: "Outfit" }}>
              Community Activity
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              See what your friends and the community have been up to
            </p>
          </div>

          {feed.length === 0 ? (
            <div className="glass-card p-12 text-center" data-testid="empty-feed">
              <Activity className="w-12 h-12 text-sky-300 mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-sky-700 dark:text-sky-300 mb-2">No activity yet</p>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Connect with friends to see their progress here
              </p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="feed-list">
              {feed.map((item, i) => {
                const Icon = FEED_ICONS[item.type] || Star;
                const colorClass = FEED_COLORS[item.type] || "text-sky-500 bg-sky-100";
                return (
                  <div
                    key={`${item.type}-${item.user_id}-${i}`}
                    className="glass-card p-4 flex items-start gap-4 transition-all hover:shadow-md"
                    data-testid={`feed-item-${item.type}`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                      <Icon className="w-5 h-5" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-sky-800 dark:text-sky-200">
                          {item.user_name}
                          {item.is_self && (
                            <span className="ml-1.5 text-xs text-sky-400">(you)</span>
                          )}
                        </p>
                      </div>
                      <p className="text-sm text-sky-700 dark:text-sky-300 mt-0.5">{item.title}</p>
                      {item.description && (
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{item.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-slate-400 flex-shrink-0">
                      <Clock className="w-3 h-3" />
                      <span>{timeAgo(item.timestamp)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default SocialFeed;
